import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Calendar, Plus } from "lucide-react"

interface ProductCalendarTabProps {
  onScheduleStock: () => void
}

export function ProductCalendarTab({ onScheduleStock }: ProductCalendarTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Stock Calendar & Metadata</CardTitle>
        <CardDescription>View stock entries by date and manage inventory metadata</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="calendar" className="space-y-4">
          <TabsList>
            <TabsTrigger value="calendar">Calendar View</TabsTrigger>
            <TabsTrigger value="entries">Stock Entries</TabsTrigger>
            <TabsTrigger value="metadata">Metadata</TabsTrigger>
          </TabsList>
          <TabsContent value="calendar" className="space-y-4">
            <div className="text-center py-12 border-2 border-dashed border-muted rounded-lg">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">Calendar View</h3>
              <p className="text-muted-foreground mb-4">
                Interactive calendar showing stock entries and scheduled inventory changes.
              </p>
              <Button onClick={onScheduleStock}>
                <Plus className="mr-2 h-4 w-4" />
                Schedule Stock Entry
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="entries" className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="font-medium">Stock In - Premium T-Shirt (Red, Large)</p>
                    <p className="text-sm text-muted-foreground">+50 units • Dec 15, 2024</p>
                  </div>
                </div>
                <Badge variant="secondary">Completed</Badge>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="font-medium">Stock In - Premium T-Shirt (Blue, Medium)</p>
                    <p className="text-sm text-muted-foreground">+30 units • Dec 20, 2024</p>
                  </div>
                </div>
                <Badge>Scheduled</Badge>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="metadata" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Inventory Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Low Stock Alert</span>
                    <span className="text-sm font-medium">10 units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Reorder Point</span>
                    <span className="text-sm font-medium">25 units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Max Stock Level</span>
                    <span className="text-sm font-medium">500 units</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Stock History</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Stock In</span>
                    <span className="text-sm font-medium text-green-600">+1,250 units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Stock Out</span>
                    <span className="text-sm font-medium text-red-600">-1,100 units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Net Change</span>
                    <span className="text-sm font-medium">+150 units</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
